import { flow } from 'fp-ts/function';
import { DateTimeLocalizationInfo } from '@mews-framework/globalization';
import { CalendarService, StructuredDate, StructuredTime, padStartZero } from '@optimus-web/core';
import { DateService, Date as MewsDate } from './dates';
import { DateTime, DateTimeService } from './dateTimes';
import { DateTimeUtc, DateTimeUtcService } from './dateTimesUtc';
import { ISOFormat } from './enums';
import { Time, TimeService } from './times';
import { WallClockDate, WallClockDateService } from './wallClockDates';

export const createDateTimeUtc = ({ date, time, timezone }: { date: WallClockDate; time: Time; timezone: string }) => {
    const parsedDate = DateService.parse(date.value, timezone);
    const dateTime = DateTimeService.fromDateAndTime(parsedDate, time);
    return DateTimeService.toUtc(dateTime);
};

export const dateTimeToWallClockDate = (dateTime: DateTime): WallClockDate => {
    const date = DateTimeService.toDate(dateTime);
    return WallClockDateService.parse(date.value);
};

export const dateTimeUtcToDate = (dateTimeUtc: DateTimeUtc, timezone: string) => {
    const dateTime = DateTimeService.fromUtc(dateTimeUtc, timezone);
    return DateTimeService.toDate(dateTime);
};

export const dateTimeUtcStringToDateISOString = (dateTimeUtcString: string, timezone: string) => {
    const dateTimeUtc = DateTimeUtcService.parse(dateTimeUtcString);
    const dateTime = DateTimeService.fromUtc(dateTimeUtc, timezone);
    const date = DateTimeService.toDate(dateTime);
    return DateService.toISOString(date);
};

export const dateTimeUtcStringToDateString = (
    dateTimeUtcString: string,
    localizationInfo: DateTimeLocalizationInfo,
    timezone: string
) => {
    const dateTimeUtc = DateTimeUtcService.parse(dateTimeUtcString);
    const dateTime = DateTimeService.fromUtc(dateTimeUtc, timezone);
    const date = DateTimeService.toDate(dateTime);
    return DateService.toString(date, localizationInfo);
};

export const dateTimeUtcStringToDateTimeString = (
    dateTimeUtcString: string,
    localizationInfo: DateTimeLocalizationInfo,
    timezone: string
) => {
    const dateTime = DateTimeService.fromUtcString(dateTimeUtcString, timezone);
    return DateTimeService.toString(dateTime, localizationInfo);
};

export const dateTimeUtcStringToDate = (dateTimeUtcString: string, timezone: string): MewsDate => {
    const dateTime = DateTimeService.fromUtcString(dateTimeUtcString, timezone);
    return DateTimeService.toDate(dateTime);
};

export const dateTimeUtcStringToTime = (dateTimeUtcString: string, timezone: string): Time => {
    const dateTime = DateTimeService.fromUtcString(dateTimeUtcString, timezone);
    return DateTimeService.toTime(dateTime);
};

export const dateTimeUtcStringToWallClockDate = (dateTimeUtcString: string, timezone: string): WallClockDate => {
    const dateTimeUtc = DateTimeUtcService.parse(dateTimeUtcString);
    const dateTime = DateTimeService.fromUtc(dateTimeUtc, timezone);
    const date = DateTimeService.toDate(dateTime);
    return WallClockDateService.parse(date.value);
};

export const dateStringToDateTimeUtcString = (
    dateString: string,
    localizationInfo: DateTimeLocalizationInfo,
    timezone: string
) => {
    const date = DateService.fromString(dateString, localizationInfo, timezone);
    const dateTime = DateTimeService.fromDate(date);
    return DateTimeService.toUtc(dateTime).value;
};

export const dateIsoStringToDateString = (dateIsoString: string, localizationInfo: DateTimeLocalizationInfo) => {
    const wallClockDate = WallClockDateService.parse(dateIsoString);
    return WallClockDateService.toString(wallClockDate, localizationInfo);
};

export const dateStringToDateIsoString = (
    dateString: string,
    localizationInfo: DateTimeLocalizationInfo,
    timezone: string
) => {
    const date = DateService.fromString(dateString, localizationInfo, timezone);
    return DateService.toISOString(date);
};

export const dateIsoStringToDateTime = (dateString: string, timezone: string) => {
    return DateTimeService.fromString(dateString, ISOFormat.Date, timezone);
};

export const structuredDateToISOString = ({ year, month, date }: StructuredDate) =>
    `${padStartZero(year, 4)}-${padStartZero(month + 1)}-${padStartZero(date)}`;

export const dateIsoStringToStructureDate = (dateIsoString: string) => {
    const date = DateService.fromString(dateIsoString, ISOFormat.Date);
    return dateToStructuredDate(date);
};

export const structuredDateToLocalISOString = (structuredDate: StructuredDate, timezone: string) => {
    const wallClockDate = structuredDateToWallClockDate(structuredDate);
    const mewsDate = DateService.parse(wallClockDate.value, timezone);
    const dateTime = DateTimeService.fromDate(mewsDate);

    return DateTimeService.toISOString(dateTime);
};

export const structuredDateToWallClockDate = (date: StructuredDate) => {
    const dateStringISO = structuredDateToISOString(date);
    return WallClockDateService.fromString(dateStringISO, ISOFormat.Date);
};

export const wallClockDateToStructuredDate = <D, M, Y>(
    date: WallClockDate,
    calendarService: CalendarService<D, M, Y>
): D =>
    // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
    calendarService.getDateFromDateString(date.value, ISOFormat.Date);

export const isValidDateIsoString = (dateIsoString: string) =>
    WallClockDateService.isValidString(dateIsoString, ISOFormat.Date);

export const dateToStructuredDate = (date: MewsDate): StructuredDate => ({
    year: DateService.getYear(date),
    month: DateService.getMonth(date),
    date: DateService.getDate(date),
});

export const dateTimeToStructuredDate = (dateTime: DateTime): StructuredDate => ({
    year: DateTimeService.getYear(dateTime),
    month: DateTimeService.getMonth(dateTime),
    date: DateTimeService.getDate(dateTime),
});

export const timeToStructuredTime = (time: Time): StructuredTime => ({
    hours: TimeService.getHours(time),
    minutes: TimeService.getMinutes(time),
    seconds: TimeService.getSeconds(time),
});

export const utcToStructuredDate = flow(DateTimeService.fromUtcString, dateTimeToStructuredDate);

export const utcToStructuredTime = flow(dateTimeUtcStringToTime, timeToStructuredTime);
/**
 * Converts an ISO date string to a Date object.
 * @param dateIsoString - The ISO date string to convert (e.g., "2023-12-25")
 * @param timezone - Optional timezone for the date.
 * @returns A Date mews object representing the parsed ISO date string
 */
export const dateIsoStringToDate = (dateIsoString: string, timezone?: string) => {
    return DateService.fromString(dateIsoString, ISOFormat.Date, timezone);
};

export const structuredTimeToTime = (structuredTime: StructuredTime): Time => {
    return TimeService.parse(
        `${padStartZero(structuredTime.hours)}:${padStartZero(structuredTime.minutes)}:${padStartZero(
            structuredTime.seconds
        )}`
    );
};

export const structuredDateAndTimeToDateTimeUtcString = (
    structuredDate: StructuredDate,
    structuredTime: StructuredTime,
    timezone: string
): string => {
    const wallClockDate = structuredDateToWallClockDate(structuredDate);
    const time = structuredTimeToTime(structuredTime);
    return createDateTimeUtc({ date: wallClockDate, time, timezone }).value;
};
